import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem,
    Table, TableHead, TableBody, TableRow, TableCell,
    Paper, CircularProgress, Alert, Stack, Pagination,
    Switch, FormControlLabel
} from '@mui/material';
import * as api from './variantsApi';
import type { Variant, VariantDetail } from './variantsApi';
import { sx13, paperSx } from '../common/detailView';


const PAGE_SIZE_DEFAULT = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface Props {
    variantId: number | null;
}

export function VariantView({ variantId }: Props) {
    const { t } = useTranslation();

    const [variant, setVariant] = useState<Variant | null>(null);
    const [variantDetails, setVariantDetails] = useState<VariantDetail[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [filter, setFilter] = useState('');

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const originalDetailsRef = useRef<VariantDetail[]>([]);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadDetails = useCallback(async (page: number, search: string, size: number) => {
        if (variantId == null) return;
        setLoading(true);
        setError(null);
        try {
            const response = await api.getVariantDetails(variantId, page, search, size);
            setVariantDetails(response.data.results);
            setTotalItems(response.data.total);
            setCurrentPage(response.data.page || page);
            setPageSize(response.data.limit || size);
            originalDetailsRef.current = JSON.parse(JSON.stringify(response.data.results));
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [variantId]);

    const loadVariant = useCallback(async () => {
        if (variantId == null) return;
        try {
            const data = await api.getVariant(variantId);
            setVariant(data);
        } catch (err) {
            setError(String(err));
        }
    }, [variantId]);

    useEffect(() => {
        loadVariant();
        loadDetails(1, '', PAGE_SIZE_DEFAULT);
    }, [loadVariant, loadDetails]);

    const handleFilterChange = (value: string) => {
        setFilter(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            loadDetails(1, value, pageSize);
        }, 500);
    };

    const handlePageChange = (_: unknown, page: number) => {
        setCurrentPage(page);
        loadDetails(page, filter, pageSize);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
        loadDetails(1, filter, newSize);
    };

    const enableEditing = () => {
        originalDetailsRef.current = JSON.parse(JSON.stringify(variantDetails));
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setVariantDetails(JSON.parse(JSON.stringify(originalDetailsRef.current)));
        setIsEditing(false);
    };

    const updateDetail = (id: number, field: 'statisticName' | 'sortOrder', value: string) => {
        setVariantDetails(prev => prev.map(d =>
            d.id === id ? { ...d, [field]: value } : d
        ));
    };

    const saveVariant = async () => {
        if (saving) return;
        setSaving(true);
        setError(null);
        try {
            const changed = variantDetails.filter(d => {
                const orig = originalDetailsRef.current.find(o => o.id === d.id);
                return !orig || orig.statisticName !== d.statisticName || orig.sortOrder !== d.sortOrder;
            });

            if (changed.length > 0) {
                await api.updateVariantDetails(changed);
            }
            originalDetailsRef.current = JSON.parse(JSON.stringify(variantDetails));
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading && !variant) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }
    if (error && !variant) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!variant) return null;

    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <Paper variant="outlined" sx={paperSx}>
            {/* Search */}
            <Box px={2} pt={1} display="flex" alignItems="center" gap={1}>
                <TextField
                    size="small"
                    placeholder={t('common.search')}
                    value={filter}
                    onChange={e => handleFilterChange(e.target.value)}
                    sx={{ flex: 1 }}
                />
            </Box>

            {/* Header */}
            <Box px={2} py={1} display="flex" alignItems="center" gap={1} bgcolor="grey.100">
                {!isEditing && (
                    <Button variant="outlined" color="inherit" size="medium" onClick={enableEditing}>
                        {t('common.edit')}
                    </Button>
                )}
                {isEditing && (
                    <>
                        <Button variant="contained" color="secondary" size="medium" onClick={saveVariant} disabled={saving}>
                            {t('common.save')}
                        </Button>
                        <Button variant="outlined" color="inherit" size="medium" onClick={cancelEditing}>
                            {t('common.cancel')}
                        </Button>
                    </>
                )}
                <Typography variant="h6" sx={{ fontSize: '14px', lineHeight: 1.43 }} fontWeight="bold">
                    {variant.variantName}
                </Typography>
                {(
                    <FormControlLabel
                        sx={{ marginLeft: 'auto' }}
                        control={<Switch checked={true} onChange={() => { localStorage.setItem('devFeature_reactVariantView', 'false'); window.location.reload(); }} size="small" sx={{
                            width: 34, height: 20, padding: 0, minWidth: 34,
                            '& .MuiSwitch-switchBase': { padding: '2px', '&.Mui-checked': { transform: 'translateX(14px)', color: '#fff', '& + .MuiSwitch-track': { backgroundColor: '#1976d2', opacity: 1 } } },
                            '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
                            '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 }
                        }} />}
                        label={<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{t('common.tryNewFeatures')}</Typography>}
                    />
                )}
                <Box flex={1} />
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={sx13}>Visa:</Typography>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={e => handlePageSizeChange(Number(e.target.value))}
                        sx={sx13}
                    >
                        {PAGE_SIZE_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {/* Content */}
            <Box p={2}>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={sx13}>{t('variantdetail.variantDetailName')}</TableCell>
                                <TableCell sx={sx13}>{t('variantdetail.statisticName')}</TableCell>
                                <TableCell sx={sx13}>{t('variantdetail.sortOrder')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {variantDetails.map(d => (
                                <TableRow key={d.id}>
                                    <TableCell sx={sx13}>{d.variantDetailName}</TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx13}>{d.statisticName}</Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={d.statisticName ?? ''}
                                                onChange={e => updateDetail(d.id, 'statisticName', e.target.value)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx13}>{d.sortOrder}</Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={d.sortOrder ?? ''}
                                                onChange={e => updateDetail(d.id, 'sortOrder', e.target.value)}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" py={1} borderTop="1px solid #eee">
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        size="small"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}
